import React, {useEffect, useState, useCallback, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useLocation} from 'react-router-dom';
import moment from 'moment';

import {setSelectedStudent} from '../redux/actions/student';
import {setSelectedClass} from '../redux/actions/class';
import {
  Classschool,
  Student as StudentType,
} from '../graphql/API';
import {getGradeByScore} from '../constants';
import SimpleLoader from '../components/SimpleLoader/SimpleLoader';
import {ClassMonthlyReportServiceWrapper} from '../modules/Report/ClassReport/ClassMonthlyReport.service';

import AppHeader from '../components/Appbar';
// @ts-ignore
import {
  StudentsArrangementTableHead,
  StudentsArrangementTableRow,
  Tabs,
} from '@PikaDoApp/pikado-web-dsm';
import {RootState} from '../redux/store';

const StudentsArrangementPageUI: React.FC<any> = ({
  dataSource,
  onRowClick,
  classes,
  selectedClassId,
  onClassChange,
}) => {

  return (
    <div className="students-arrangement-page">
      {/* Old AppBar Header */}
      <AppHeader />

      {/* Class selection tabs */}
      {classes.length > 1 && (
        <div className="w-full max-w-full overflow-x-auto">
          <style>{`
            .students-arrangement-page .overflow-x-auto nav {
              justify-content: flex-start !important;
            }
          `}</style>
          <Tabs
            items={classes.map((cls: Classschool) => ({
              key: cls.id,
              label: `Class ${cls.name}`,
              children: null,
            }))}
            activeKey={selectedClassId}
            onChange={onClassChange}
          />
        </div>
      )}

      {/* Students table */}
      <div className="overflow-x-auto">
        <style>{`
          .students-arrangement-page thead tr {
            height: 60px;
          }
        `}</style>
        <table className="w-full min-w-full table-fixed">
          <StudentsArrangementTableHead
            arrLabel="Rank"
            studentNameLabel="Student Name"
            totalPerformanceLabel="Monthly Performance"
            monthPerformanceLabel="Score"
            monthAbsenceLabel="Absences"
            monthDoneLabel="Completed"
          />
          <tbody>
            {dataSource.map((record: any) => (
              <StudentsArrangementTableRow
                key={record.key}
                record={record}
                onClick={() => onRowClick(record)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

interface LocationState {
  classId: string;
  cacheKey?: string;
}

interface StudentRecord extends StudentType {
  key: string;
  arr: number;
  gradeColor: string;
  grade: string;
  statistic: StudentType['statistic'];
  totalPerformance: number;
  monthPerformance: {
    average: number;
    performanceRate?: 'increase' | 'decrease';
  };
  monthAbsence: {
    absence: number;
    absenceRate?: 'increase' | 'decrease';
  };
  monthDone: number;
}

// Create service instance outside component to avoid recreation
const classReportService = new ClassMonthlyReportServiceWrapper();

const StudentsArrangementPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // 1) Grab any initial classId/report from location.state
  const state = location.state as LocationState | undefined;
  const initialClassIdFromState = state?.classId ?? null;

  // 2) Read selectedClassId from Redux
  const selectedClassId = useSelector(
    (s: RootState) => s.classState.selectedClassId,
  );

  // — only seed Redux from URL if Redux has no selection yet
  useEffect(() => {
    if (!selectedClassId && initialClassIdFromState) {
      dispatch(setSelectedClass(initialClassIdFromState));
    }
  }, [initialClassIdFromState, selectedClassId, dispatch]);

  // 3) Grab teacher & school → classes array
  //@ts-ignore
  const teacher = useSelector((s: RootState) => s.user.teacher);
  const school = useSelector((s: RootState) => s.user.school);
  const activeEntity = teacher || school;
  const classes: Classschool[] = useMemo(
    () => activeEntity?.classesschool ?? [],
    [activeEntity],
  );

  // Get the current month in YYYY-MM format
  const currentMonth = useMemo(() => moment().format('YYYY-MM'), []);

  // Build cache key for classMonthlyReportsByMonth
  const cacheKey = useMemo(() => {
    if (!selectedClassId) return null;
    return `${selectedClassId}-${currentMonth}`;
  }, [selectedClassId, currentMonth]);


  // Get studentsMonthlyReports from Redux using the cache key
  const classMonthlyReportByMonth = useSelector(
    (s: RootState) => cacheKey ? s.fullReport?.classMonthlyReportsByMonth?.[cacheKey] : null
  );

  const studentsMonthlyReports = useMemo(() => {
    return classMonthlyReportByMonth?.studentsMonthlyReports || [];
  }, [classMonthlyReportByMonth]);

  // 4) Local: loading flag
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // 5) Default to first class if none selected
  useEffect(() => {
    if (!selectedClassId && classes.length > 0) {
      dispatch(setSelectedClass(classes[0].id));
    }
  }, [classes, selectedClassId, dispatch]);

  // 6) Fetch data when not available in cache
  useEffect(() => {
    if (!selectedClassId || !classes.length || !cacheKey) {
      setIsLoading(false);
      return;
    }

    // Check if we have data in Redux
    if (classMonthlyReportByMonth) {
      const studentsArray = classMonthlyReportByMonth.studentsMonthlyReports || [];
      console.log(`✅ [StudentsArrangement] Found cached data for ${cacheKey}:`, {
        reportId: classMonthlyReportByMonth._id,
        month: classMonthlyReportByMonth.month,
        studentsCount: studentsArray.length,
        sampleStudent: studentsArray[0] ? {
          studentId: studentsArray[0].studentId,
          name: studentsArray[0].student?.name,
          rank: studentsArray[0].monthlyRankInClass,
          average: studentsArray[0].studentMonthlyAverage
        } : null
      });
      setIsLoading(false);
    } else {
      setIsLoading(true);

      const fetchData = async () => {
        try {
          await classReportService.getClassMonthlyReportByClassIdAndMonth(
            selectedClassId,
            currentMonth
          );
          // Data will be stored in Redux by the service, triggering a re-render
          setIsLoading(false);
        } catch (error) {
          console.error('❌ [StudentsArrangement] Error fetching class monthly report:', error);
          setIsLoading(false);
        }
      };

      fetchData();
    }
  }, [selectedClassId, classes, classMonthlyReportByMonth, cacheKey, currentMonth]);

  // 7) Build the table dataSource using studentsMonthlyReports from Redux
  const dataSource = useMemo(() => {
    console.log('📊 [StudentsArrangement] Building dataSource...', {
      hasMonthlyReports: !!studentsMonthlyReports,
      studentsCount: studentsMonthlyReports?.length || 0,
      cacheKey: cacheKey
    });

    if (!studentsMonthlyReports || studentsMonthlyReports.length === 0) {
      console.warn('⚠️ [StudentsArrangement] No studentsMonthlyReports data available');
      return [];
    }

    // Build data for each student from studentsMonthlyReports
    const result = studentsMonthlyReports
      .filter((studentReport: any) => studentReport.student != null) // Filter out reports with null students
      .map((studentReport: any) => {
        const student = studentReport.student;
        const monthlyAverage = studentReport.studentMonthlyAverage ?? 0;
        const {color: gradeColor, grade} = getGradeByScore(monthlyAverage);

        // Calculate performance rate from absenceImprovement
        const performanceRate = studentReport.absenceImprovement
          ? (studentReport.absenceImprovement > 0 ? 'increase' : 'decrease')
          : undefined;

        const record = {
          ...student,
          key: student.id || studentReport.studentId,
          arr: studentReport.monthlyRankInClass ?? 0,
          totalPerformance: monthlyAverage.toFixed(2),
          monthPerformance: {
            average: studentReport.studentMonthlyScore ?? 0,
            performanceRate: performanceRate as any,
          },
          monthAbsence: {
            absence: studentReport.absenceDays ?? 0,
            absenceRate: studentReport.absenceDays > 0 ? ('decrease' as any) : undefined,
          },
          monthDone: studentReport.attempts ?? 0,
          gradeColor,
          grade,
          statistic: student.statistic || null,
        } as StudentRecord;

        return record;
      });

    // Sort by total score (higher score = better performance)
    const sortedResult = result.sort((a, b) => b.monthPerformance.average - a.monthPerformance.average);

    // Re-assign ranks based on total score sorting (1 = highest score)
    const rankedResult = sortedResult.map((record, index) => ({
      ...record,
      arr: index + 1, // Rank starts at 1
    }));

    console.log('✅ [StudentsArrangement] DataSource built successfully:', {
      totalStudents: rankedResult.length,
      top3Students: rankedResult.slice(0, 3).map(s => ({
        name: s.name,
        rank: s.arr,
        monthlyPerformance: s.totalPerformance,
        score: s.monthPerformance.average
      }))
    });

    return rankedResult;
  }, [studentsMonthlyReports, cacheKey]);

  // 8) Row click → dispatch student and navigate
  const onRowClick = useCallback(
    (record: StudentRecord) => {
      dispatch(setSelectedStudent(record));
      if (record) {
        navigate(`/student/${record.key}`);
      } else {
        alert('No Report for this student yet');
      }
    },
    [dispatch, navigate],
  );

  // 9) Class change → dispatch new class
  const onClassChange = useCallback(
    (key: string) => {
      console.log('🔄 [StudentsArrangement] Class changed to:', key);
      dispatch(setSelectedClass(key));
      setIsLoading(true); // Show loader while new class data loads
    },
    [dispatch],
  );

  if (isLoading && dataSource.length === 0) {
    return <SimpleLoader isLoading={true} title="Loading class data..." />;
  }

  return (
    <StudentsArrangementPageUI
      dataSource={dataSource}
      onRowClick={onRowClick}
      classes={classes}
      selectedClassId={selectedClassId || (classes[0]?.id ?? '')}
      onClassChange={onClassChange}
    />
  );
};

export default StudentsArrangementPage;
